import { Navigate, useParams, useSearchParams } from 'react-router';
import { route } from '@hero/web-hooks';

/**
 * One-release back-compat shim for the legacy `/financing/partner/:appId?p=...`
 * route used by invite emails sent before the rename to
 * `/financing-application/:appId?i=...`.
 *
 * Reads the legacy `?p=` query param, rewrites it to the canonical
 * `?i=`, and renders a client-side `<Navigate>` so the rest of the
 * URL (and any deep-linked anchor) follows along. The companion
 * backend redirect lives in `FinancingController.partnerLegacyRedirect`
 * for any non-browser tooling that hits the old route directly.
 *
 * Drop this once the staging+production lender inboxes have cycled
 * through their open-link window (~30 days after the rename ships).
 */
export default function FinancingPartnerRedirect() {
  const { appId } = useParams<{ appId: string }>();
  const [searchParams] = useSearchParams();
  /**
   * Preserve the legacy `?p=` value (lender id) under the new `?i=`
   * key. We accept whichever is set so a malformed legacy link
   * still has a chance of routing rather than 404ing.
   */
  const legacyLenderId = searchParams.get('p') ?? searchParams.get('i') ?? '';

  if (!appId) {
    return <Navigate to={route('financing')} replace />;
  }

  const target = legacyLenderId
    ? `${route('financing_lender_view', { appId })}?i=${encodeURIComponent(
        legacyLenderId,
      )}`
    : route('financing_lender_view', { appId });

  return <Navigate to={target} replace />;
}
